import {
  Box,
  FormControl,
  FormLabel,
  Select,
  SelectProps,
  Typography,
  IconButton,
  Button,
  MenuItem,
} from "@mui/material";
import { Control, Controller, ControllerProps } from "react-hook-form";
import React, { ReactNode, useState } from "react";
import { StyledMenu } from "@/components/styled";
import SvgSelectArrowUp from "@/assets/icons/SVGSelectArrowUp.tsx";
import SvgSelectArrowDown from "@/assets/icons/SVGSelectArrowDown.tsx";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import TooltipInfoIcon from "../TooltipInfoIcon";

type FormSelectProps = SelectProps & {
  name: string;
  control: Control<any, any>;
  controlProps?: Partial<ControllerProps>;
  label?: string;
  children: ReactNode;
  labelTooltip?: string;
  valueLabelMap?: Record<string, string>;
};

const FormSelect = ({
  name,
  control,
  controlProps,
  label,
  children,
  labelTooltip,
  valueLabelMap,
  ...props
}: FormSelectProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isRequired = !!controlProps?.rules?.required;

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend">
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: (theme) => theme.palette.textGray1,
            paddingBottom: "2px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {label}{" "}
          {isRequired && <Typography sx={{ color: "red" }}>*</Typography>}
          {labelTooltip && (
            <Box sx={{ marginLeft: "4px" }}>
              <TooltipInfoIcon tooltipText={labelTooltip} />
            </Box>
          )}
        </Box>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        {...controlProps}
        render={({ field: { onChange, value } }) => (
          <>
            <Button
              variant="outlinedGray"
              endIcon={open ? <SvgSelectArrowUp /> : <SvgSelectArrowDown />}
              sx={{
                textTransform: "none",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                borderRadius: "4px",
                "& .MuiButton-endIcon": {
                  marginLeft: "auto",
                },
              }}
              disabled={props?.disabled}
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
                setOpen(!open);
              }}
            >
              {value?.length > 0 ? (
                <Typography
                  variant={"subtitleRegular"}
                  sx={{
                    color: "textGray2",
                    ...(props?.disabled && { color: "rgb(0 0 0 / 38%)" }),
                  }}
                >
                  {valueLabelMap && valueLabelMap[value]
                    ? valueLabelMap[value]
                    : capitalizeFirstLetter(value)}
                </Typography>
              ) : (
                <Typography
                  variant={"subtitleRegular"}
                  sx={{
                    color: "#888684",
                    fontWeight: 400,
                    fontSize: "14px",
                  }}
                >
                  {props?.placeholder}
                </Typography>
              )}
            </Button>
            <StyledMenu
              anchorEl={anchorEl}
              sx={{
                "& .MuiPaper-root": { width: anchorEl?.offsetWidth },
              }}
              open={open}
              onClose={() => {
                setOpen(false);
                setAnchorEl(null);
              }}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child as React.ReactElement<any>, {
                    onClick: () => {
                      onChange(child.props.value);
                      setOpen(false);
                      setAnchorEl(null);
                    },
                  });
                }
                return child;
              })}
            </StyledMenu>
          </>
        )}
      />
    </FormControl>
  );
};

export default FormSelect;
